// Basic
import { useEffect } from 'react';
import PropTypes from 'prop-types';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
// Common
import ICONS from '@/common-utils/enums/icons';
import customizationFields from '../../../../fields/customizationFields';
import isOptionChecked from '../../../../utils/isOptionChecked';
// Lang
import { nsCustomization } from '@/app/i18n/namespaces';
import CheckboxHelper from '@/common-utils/components/renders/checkbox/CheckboxHelper';

/**
 * @param root0
 * @param root0.checkedServicesAndApps
 * @param root0.setCheckedServicesAndApps
 * @param root0.checkedOptions
 * @param root0.setCheckedOptions
 * @param root0.customization
 * @param root0.isLoading
 * @param root0.isDisabled
 * @param root0.isExternalTrigger
 * @returns {JSX} ServicesAndAppsLock
 */
function ServicesAndAppsLock({
    checkedServicesAndApps = [],
    setCheckedServicesAndApps,
    checkedOptions = [],
    customization,
    isLoading = false,
    isDisabled = false,
    isExternalTrigger = false
}) {

    // Constants
    const entityPrefix = 'details.functionnalities.lock';
    const initialOptions = [
        customizationFields.MPF_OPTIONS.KIOSK_MODE.key,
        customizationFields.MPF_OPTIONS.BURGER_MENU_LOCK.key
    ];


    // Hooks
    // Initialize checkedOptions from customization if empty
    useEffect(() => {
        if (customization?.mpfOptions?.length) {
            const enabledOptions =
                customization[customizationFields.MPF_OPTIONS.key] || [];

            const defaults = initialOptions.filter((key) =>
                enabledOptions.some((item) => item.name === key)
            );

            setCheckedServicesAndApps(defaults.map((key) => ({ name: key, value: null })));
        }
    }, [customization]);

    // Functions -- Toggle option
    const toggleOption = (key) => {
        const isChecked = checkedOptions.some((item) => item.name === key);

        if (isChecked) {
            setCheckedServicesAndApps(checkedServicesAndApps.filter((item) => item.name !== key));
        } else {
            setCheckedServicesAndApps([...checkedServicesAndApps, { name: key, value: null }]);
        }
    };

    // Constants
    const donationsOrAutomateModesChecked = isOptionChecked(checkedOptions, customizationFields.MPF_OPTIONS.DONATIONS_MODE.key)
        || isOptionChecked(checkedOptions, customizationFields.MPF_OPTIONS.AUTOMATE_MODE.key);

    // Hooks
    useEffect(() => {
        // Check Burger menu lock by default when Donations or Automate modes are checked
        if (donationsOrAutomateModesChecked && !isOptionChecked(checkedServicesAndApps, customizationFields.MPF_OPTIONS.BURGER_MENU_LOCK.key)) {
            setCheckedServicesAndApps([...checkedServicesAndApps, { name: customizationFields.MPF_OPTIONS.BURGER_MENU_LOCK.key, value: null }]);
        }
    }, [donationsOrAutomateModesChecked]);

    return (
        <div className='p-4 space-y-4'>
            {isLoading &&
                (
                    <>
                        {[1, 2].map((i) => (
                            <div key={i} className='space-y-2'>
                                <div className='flex items-start gap-3'>
                                    <Skeleton className='h-4 w-64 bg-gray-200' />
                                </div>
                            </div>
                        ))}
                    </>
                )
            }
            {!isLoading &&
                (<>
                    <CheckboxHelper
                        isChecked={isOptionChecked(checkedServicesAndApps, customizationFields.MPF_OPTIONS.KIOSK_MODE.key)}
                        onCheckedChange={() => toggleOption(customizationFields.MPF_OPTIONS.KIOSK_MODE.key)}
                        isDisabled={true}
                        entity={`${entityPrefix}.kioskMode`}
                        namespace={nsCustomization}
                    />
                    <div className='md:grid lg:flex lg:space-x-4'>
                        <div key={customizationFields.MPF_OPTIONS.BURGER_MENU_LOCK.key} className='flex items-start gap-3'>
                            <CheckboxHelper
                                isChecked={isOptionChecked(checkedServicesAndApps, customizationFields.MPF_OPTIONS.BURGER_MENU_LOCK.key)}
                                onCheckedChange={() => toggleOption(customizationFields.MPF_OPTIONS.BURGER_MENU_LOCK.key)}
                                isDisabled={isDisabled || donationsOrAutomateModesChecked}
                                entity={`${entityPrefix}.burgerMenuLock.text`}
                                namespace={nsCustomization}
                            />
                            <TooltipHelper
                                trigger={
                                    <Icon icon={ICONS.HELP} />
                                }
                                content={
                                    <div className='max-w-96'>
                                        <Entity entity={`${entityPrefix}.burgerMenuLock.tooltip`} namespace={nsCustomization} />
                                    </div>
                                }
                            />
                        </div>
                        {isExternalTrigger && !isOptionChecked(checkedServicesAndApps, initialOptions[1]) &&
                            (
                                <div className='flex-1 flex items-start gap-1 text-sm text-warning mt-2'>
                                    <Icon icon={ICONS.WARNING} />
                                    <Entity entity={`${entityPrefix}.warning`} namespace={nsCustomization} />
                                </div>
                            )
                        }
                    </div>
                </>
                )
            }
        </div>
    );
}

ServicesAndAppsLock.propTypes = {
    checkedServicesAndApps: PropTypes.array,
    setCheckedServicesAndApps: PropTypes.func,
    checkedOptions: PropTypes.array,
    customization: PropTypes.object,
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isExternalTrigger: PropTypes.bool
};
export default ServicesAndAppsLock;